import { zodResolver } from '@hookform/resolvers/zod';
import { DialogDescription } from '@radix-ui/react-dialog';
import { AxiosError } from 'axios';
import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { FormGroup } from '@/components/FormGroup';
import { MonacoCodeEditor } from '@/components/MonacoEditor';
import { RequiredLabel } from '@/components/RequiredLabel';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { useUpdatePrompt } from '@/hooks/useUpdatePrompt';
import { FilterPromptsDto, Prompt } from '@/services/PromptsService';

const schema = z.object({
  promptName: z.string().min(1, 'Required'),
  serviceName: z.string().min(1, 'Required'),
  template: z.string().min(1, 'Required'),
  responseSchema: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onClose: () => void;
  prompt: Prompt | null;
  onSuccess?: () => void;
  filter: FilterPromptsDto;
}

export function EditPromptForm({
  open,
  onClose,
  prompt,
  onSuccess,
  filter,
}: Props) {
  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      promptName: '',
      serviceName: '',
      template: '',
      responseSchema: '',
    },
  });

  const { updatePrompt, isUpdating } = useUpdatePrompt(filter);

  useEffect(() => {
    if (prompt && open) {
      reset({
        promptName: prompt.promptName,
        serviceName: prompt.serviceName,
        template: prompt.template,
        responseSchema: prompt.responseSchema
          ? JSON.stringify(prompt.responseSchema, null, 2)
          : '',
      });
    }
  }, [prompt, open, reset]);

  async function onSubmit(data: FormData) {
    if (!prompt?.id) {
      toast.error('Missing prompt ID');
      return;
    }

    try {
      let parsedResponseSchema: Record<string, any> | undefined;
      if (data.responseSchema && data.responseSchema.trim()) {
        try {
          parsedResponseSchema = JSON.parse(data.responseSchema);
        } catch (e) {
          setError('responseSchema', {
            message: 'Invalid JSON format',
          });
          return;
        }
      }

      const updateData = {
        promptName: data.promptName,
        serviceName: data.serviceName,
        template: data.template,
        responseSchema: parsedResponseSchema,
      };

      await updatePrompt({ id: prompt.id, data: updateData });
      toast.success('Prompt updated');
      onClose();
      onSuccess?.();
    } catch (error) {
      toast.error(
        error instanceof AxiosError
          ? error.response?.data?.message
          : 'Update failed',
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Edit Prompt</DialogTitle>
          <DialogDescription>
            Update the details for this prompt in lower case.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormGroup error={{ message: errors.promptName?.message }}>
              <RequiredLabel htmlFor="promptName">Prompt Name</RequiredLabel>
              <Controller
                name="promptName"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
            </FormGroup>

            <FormGroup error={{ message: errors.serviceName?.message }}>
              <RequiredLabel htmlFor="serviceName">Service Name</RequiredLabel>
              <Controller
                name="serviceName"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
            </FormGroup>
          </div>

          <Tabs defaultValue="template" className="w-full">
            <TabsList className="w-full">
              <TabsTrigger value="template" className="flex-1">
                Template
              </TabsTrigger>
              <TabsTrigger value="schema" className="flex-1">
                Response Schema
              </TabsTrigger>
            </TabsList>

            <TabsContent value="template">
              <FormGroup
                className="space-y-2"
                error={{ message: errors.template?.message }}
              >
                <RequiredLabel htmlFor="template">Template</RequiredLabel>
                <Controller
                  name="template"
                  control={control}
                  render={({ field }) => (
                    <MonacoCodeEditor
                      value={field.value}
                      onChange={(val) => field.onChange(val)}
                    />
                  )}
                />
              </FormGroup>
            </TabsContent>

            <TabsContent value="schema">
              <FormGroup
                className="space-y-2"
                error={{ message: errors.responseSchema?.message }}
              >
                <label htmlFor="responseSchema" className="text-sm font-medium">
                  Response Schema (JSON)
                </label>
                <Controller
                  name="responseSchema"
                  control={control}
                  render={({ field }) => (
                    <MonacoCodeEditor
                      value={field.value || ''}
                      placeholder={`{\n  "type": "object",\n  "properties": {\n    "name": {\n      "type": "string"\n    }\n  }\n}`}
                      onChange={(val) => field.onChange(val)}
                      language="json"
                    />
                  )}
                />
              </FormGroup>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button variant="ghost" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isUpdating}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
